import React from "react";

/**
 * Top-level error boundary.
 *
 * Without a boundary, any render-time exception unmounts the whole React tree
 * and the user is left staring at a blank white screen (e.g. a dropped import
 * throwing a ReferenceError). This catches those errors, shows a recoverable
 * fallback, and logs a PHI-safe error report (no patient/app data — only the
 * error name, message, and stack).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Never log app/patient data — only the error itself (no props/state).
    // eslint-disable-next-line no-console
    console.error(
      "Unhandled UI error:",
      error?.name,
      error?.message,
      info?.componentStack,
    );
    try {
      // PostHog US only (absent on CIS). Do not attach custom PHI properties.
      if (typeof window !== "undefined" && window.posthog?.captureException) {
        window.posthog.captureException(error);
      }
    } catch (_) {
      /* analytics must never block the fallback */
    }
  }

  handleReload = () => {
    // Full reload re-runs providers from a clean slate.
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#0f172a",
          color: "#e2e8f0",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        }}
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              color: "#94a3b8",
              marginBottom: 20,
            }}
          >
            The page failed to load. Please reload — your session is preserved.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              border: "1px solid rgba(201,150,59,0.4)",
              background: "rgba(201,150,59,0.15)",
              color: "#e2c07a",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Reload
          </button>
          {isDev && this.state.error && (
            <pre
              style={{
                marginTop: 20,
                padding: 12,
                textAlign: "left",
                fontSize: 12,
                color: "#fca5a5",
                background: "rgba(0,0,0,0.3)",
                borderRadius: 8,
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
